import { trackBeginCheckout, trackEvent } from "@/lib/analytics";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Loader2, Sparkles, Clock, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { useMentoringWallet } from "@/hooks/useMentoringWallet";
import { useMentoringCancellation } from "@/hooks/useMentoringCancellation";
import { useVipPricing, type VipTierKey } from "@/hooks/useVipPricing";
import { useNavigate } from "@/lib/router-compat";

export interface SessionBookingFlowProps {
  /** Whether the flow is visible (drives the availability fetch). */
  active: boolean;
  /** Close the containing surface (dialog or popover). */
  onClose: () => void;
  /** Compact variant used inside the mentoring popover. */
  variant?: "dialog" | "inline";
}

interface Slot { start: string; end: string; }

const sameLocalDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Booking flow (quota check, duration, calendar, slots, confirm).
 * Rendered inside a Dialog (RequestSessionModal) or inline inside the
 * mentoring popover so the booking never opens a separate pop-up.
 */
const SessionBookingFlow = ({ active, onClose, variant = "dialog" }: SessionBookingFlowProps) => {
  const { t, lang } = useLanguage();
  const { wallet, loading: walletLoading } = useMentoringWallet();
  const { cancellationDate } = useMentoringCancellation();
  const es = lang === "es";
  const pricing = useVipPricing(es, "addon");
  const navigate = useNavigate();
  const inline = variant === "inline";
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [day, setDay] = useState<Date | undefined>(undefined);
  const [selected, setSelected] = useState<string | null>(null);
  const [topic, setTopic] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [duration, setDuration] = useState<1 | 2>(1);

  const remainingMin = wallet?.totalRemaining ?? 0;
  const totalMin = wallet?.totalAvailable ?? 0;
  const hasQuota = remainingMin >= 60;
  const can2h = remainingMin >= 120;
  const hasPlan = !!wallet?.hasPlan;
  const extraRemainingMin = Math.max(0, (wallet?.extraMinutes ?? 0) - (wallet?.extraUsed ?? 0));

  const rolloverMin = wallet?.rolloverMinutes ?? 0;
  const planMin = wallet?.planMinutes ?? 0;
  const hasRollover = rolloverMin > 0;

  const tipKey: string = useMemo(() => {
    const k = (wallet?.planKey || "").toLowerCase();
    if (k.includes("arranque") || k.includes("brujula")) return hasRollover ? "tipArranqueRollover" : "tipArranque";
    if (k.includes("crucero") || k.includes("sherpa")) return hasRollover ? "tipCruceroRollover" : "tipCrucero";
    if (k.includes("partner") || k.includes("fractional")) return hasRollover ? "tipPartnerRollover" : "tipPartner";
    return "tipGeneric";
  }, [wallet?.planKey, hasRollover]);

  const tipText = useMemo(() => {
    const raw = t("mentoringBooking", tipKey);
    return raw.replace("{h}", (rolloverMin / 60).toFixed(rolloverMin % 60 === 0 ? 0 : 1));
  }, [t, tipKey, rolloverMin]);

  useEffect(() => {
    if (!active || !hasQuota) return;
    setLoadingSlots(true);
    setSlots(null);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("calendar-availability", { body: { days: 45 } });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        setSlots(data?.slots ?? []);
      } catch (e) {
        console.error("Detalle del error de Google Calendar:", e);
        toast({ title: "Error", description: t("mentoringBooking", "bookingErrorHuman"), variant: "destructive" });
        setSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    })();
  }, [active, hasQuota]);

  const allStarts = useMemo(() => new Set((slots ?? []).map((s) => new Date(s.start).getTime())), [slots]);

  const daySlots = useMemo(() => {
    if (!slots || !day) return [];
    const sameDay = slots.filter((s) => sameLocalDay(new Date(s.start), day));
    if (duration === 1) return sameDay;
    return sameDay.filter((s) => {
      const startMs = new Date(s.start).getTime();
      const nextMs = startMs + 60 * 60 * 1000;
      if (!allStarts.has(nextMs)) return false;
      return sameLocalDay(new Date(nextMs), day);
    });
  }, [slots, day, duration, allStarts]);

  const enabledDays = useMemo(() => {
    const eligible = (slots ?? []).filter((s) => {
      if (duration === 1) return true;
      const startMs = new Date(s.start).getTime();
      const nextMs = startMs + 60 * 60 * 1000;
      return allStarts.has(nextMs) && sameLocalDay(new Date(startMs), new Date(nextMs));
    });
    return new Set(eligible.map((s) => {
      const d = new Date(s.start);
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    }));
  }, [slots, duration, allStarts]);

  useEffect(() => { if (duration === 2 && !can2h) setDuration(1); }, [duration, can2h]);
  useEffect(() => { setSelected(null); }, [duration]);

  // Detect if selected day is in a month past the user's scheduled cancellation
  const cancellationWarning = useMemo(() => {
    if (!day || !cancellationDate) return null;
    const firstOfSelectedMonth = new Date(day.getFullYear(), day.getMonth(), 1);
    const firstOfCurrentMonth = new Date();
    firstOfCurrentMonth.setDate(1);
    firstOfCurrentMonth.setHours(0, 0, 0, 0);
    if (firstOfSelectedMonth <= firstOfCurrentMonth) return null;
    if (cancellationDate >= firstOfSelectedMonth) return null;
    const locale = lang === "es" ? "es-ES" : "en-GB";
    const dateStr = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Madrid" }).format(cancellationDate);
    const monthStr = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "Europe/Madrid" }).format(firstOfSelectedMonth);
    return t("mentoringBooking", "nextMonthCancellationWarning")
      .replace("{date}", dateStr)
      .replace("{month}", monthStr);
  }, [day, cancellationDate, lang, t]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("request-session-booking", {
        body: { start: selected, topic, durationHours: duration },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      trackEvent("book_session");
      toast({ title: t("mentoringBooking", "successTitle"), description: t("mentoringBooking", "successDesc") });
      onClose();
      setSelected(null); setTopic(""); setDay(undefined);
    } catch (e) {
      console.error("Detalle del error de Google Calendar:", e);
      toast({ title: "Error", description: t("mentoringBooking", "bookingErrorHuman"), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const fmtTime = (iso: string) =>
    new Intl.DateTimeFormat(lang === "es" ? "es-ES" : "en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" }).format(new Date(iso));

  const fmtH = (m: number) => {
    const h = m / 60;
    return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1).replace(/\.0$/, "")}h`;
  };

  return (
    <>
      {/* Scrollable body */}
      <div className={cn("flex-1 overflow-y-auto space-y-4", inline ? "px-3.5 py-3.5" : "px-5 py-4")}>
        {walletLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : !hasPlan && !hasQuota ? (
          <div className="space-y-4 py-2">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 mx-auto rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-foreground" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-lg leading-tight">{t("mentoringBooking", "noPlanTitle")}</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
                {t("mentoringBooking", "noPlanDesc")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {(["arranque", "crucero", "partner"] as VipTierKey[]).map((key, idx) => {
                const tier = pricing.tiers[key];
                const meta = (t("mentoringBooking", "plans") as any)?.[key]?.[lang] ?? { name: key, pace: "", extra: "" };
                const isRecommended = idx === 1;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      onClose();
                      window.location.href = (es ? "/mentoria#planes-vip" : "/mentoring#planes-vip") + `?tier=${key}`;
                    }}
                    className={cn(
                      "text-left rounded-2xl border bg-card p-4 transition-all hover:border-primary hover:shadow-xs min-h-[44px] group relative",
                      isRecommended ? "border-primary/70 bg-primary/5" : "border-border"
                    )}
                  >
                    {isRecommended && (
                      <span className="absolute -top-2 left-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-foreground text-[10px] font-bold uppercase tracking-wide">
                        <Sparkles className="w-3 h-3" strokeWidth={2} />
                        {es ? "Recomendado" : "Recommended"}
                      </span>
                    )}
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <span className="font-display font-bold text-base">{meta.name}</span>
                      <span className="font-body text-sm tabular-nums">
                        <span className="font-extrabold">{tier.monthly.label}</span>
                        <span className="text-muted-foreground">{tier.monthly.period}</span>
                      </span>
                    </div>
                    <p className="font-body text-xs text-foreground/90 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-foreground shrink-0" strokeWidth={1.75} />
                      {meta.pace}
                    </p>
                    {meta.extra && (
                      <p className="font-body text-[11px] text-muted-foreground mt-1.5 leading-snug">{meta.extra}</p>
                    )}
                    <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-foreground/70 group-hover:text-foreground">
                      {t("mentoringBooking", "noPlanPickCta")} →
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => { onClose(); window.location.href = es ? "/mentoria#planes-vip" : "/mentoring#planes-vip"; }}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                {t("mentoringBooking", "upgradePlan")}
              </button>
            </div>
          </div>
        ) : !hasQuota ? (
          <div className="text-center space-y-3 py-8">
            <h3 className="font-display text-lg">{t("mentoringBooking", "noQuotaTitle")}</h3>
            <p className="font-body text-sm text-muted-foreground">{t("mentoringBooking", "noQuotaDesc")}</p>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                className="rounded-full bg-primary text-foreground hover:bg-primary/90 font-bold min-h-[44px]"
                onClick={async () => {
                  const { data: { session } } = await supabase.auth.getSession();
                  const r = await fetch(`${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/create-extra-hours-checkout`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${session?.access_token}`, "Content-Type": "application/json" },
                    body: JSON.stringify({ hours: 1 }),
                  });
                  const j = await r.json();
                  if (j.url) {
                    trackBeginCheckout({ source: "reserva_sesion", productType: "extra_hours", productKey: "1h", interval: "one_time" });
                    window.location.href = j.url;
                  }
                }}
              >
                {t("mentoringBooking", "buyExtra")}
              </Button>
              <Button variant="outline" className="rounded-full min-h-[44px]" onClick={() => { onClose(); window.location.href = lang === "es" ? "/mentoria#planes-vip" : "/mentoring#planes-vip"; }}>
                {t("mentoringBooking", "upgradePlan")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Compact quota + tip banner */}
            <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="inline-flex items-center gap-1.5 font-body text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {t("mentoringBooking", "quotaLabel")}
                </span>
                <span className="font-figures font-bold tabular-nums">
                  {fmtH(remainingMin)} <span className="text-muted-foreground font-normal">/ {fmtH(totalMin)}</span>
                </span>
              </div>
              {hasRollover && (
                <p className="font-body text-[11px] text-muted-foreground">
                  {fmtH(planMin)} {lang === "es" ? "plan" : "plan"} + {fmtH(rolloverMin)} {lang === "es" ? "acumuladas" : "rolled over"}
                </p>
              )}
              {extraRemainingMin > 0 && (
                <p className="font-body text-[11px] text-muted-foreground">
                  {fmtH(extraRemainingMin)}{" "}
                  {es ? "de consultas puntuales · no caducan" : "in one-off consultations · never expire"}
                </p>
              )}
            </div>

            {!inline && (
              <div className="rounded-xl bg-primary/10 border border-primary/30 p-3 flex gap-2.5">
                <Sparkles className="w-4 h-4 text-foreground shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="font-body text-xs leading-relaxed">{tipText}</p>
              </div>
            )}

            {/* Duration selector */}
            <div className="space-y-1.5">
              <p className="font-body text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("mentoringBooking", "durationLabel")}</p>
              <ToggleGroup
                type="single"
                value={String(duration)}
                onValueChange={(v) => { if (v === "1" || v === "2") setDuration(Number(v) as 1 | 2); }}
                className="w-full grid grid-cols-2 gap-2"
              >
                <ToggleGroupItem value="1" className="w-full rounded-full border border-border data-[state=on]:bg-primary data-[state=on]:text-foreground data-[state=on]:font-bold min-h-[44px]">
                  {t("mentoringBooking", "duration1h")}
                </ToggleGroupItem>
                <ToggleGroupItem value="2" disabled={!can2h} className="w-full rounded-full border border-border data-[state=on]:bg-primary data-[state=on]:text-foreground data-[state=on]:font-bold min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed">
                  {t("mentoringBooking", "duration2h")}
                </ToggleGroupItem>
              </ToggleGroup>
              {!can2h && (
                <p className="font-body text-[11px] text-muted-foreground">{t("mentoringBooking", "duration2hDisabled")}</p>
              )}
            </div>

            {/* Calendar (compact) */}
            <div className="space-y-1.5">
              <p className="font-body text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("mentoringBooking", "pickDay")}</p>
              <div className="rounded-xl border border-border bg-card">
                <Calendar
                  mode="single"
                  selected={day}
                  onSelect={(d) => { setDay(d ?? undefined); setSelected(null); }}
                  disabled={(d) => {
                    const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                    return !enabledDays.has(k);
                  }}
                  className={cn(
                    "w-full p-2 pointer-events-auto",
                    inline
                      ? "[&_.rdp-day_button]:h-7 [&_.rdp-day_button]:w-7"
                      : "",
                  )}

                />
              </div>
            </div>

            {/* Cancellation warning when picking a month beyond subscription end */}
            {cancellationWarning && (
              <div className="rounded-xl border border-amber-300 bg-amber-50/70 dark:bg-amber-900/20 p-3 flex gap-2.5">
                <AlertTriangle className="w-4 h-4 text-foreground shrink-0 mt-0.5" strokeWidth={1.75} />
                <div className="space-y-2 min-w-0">
                  <p className="font-body text-xs leading-relaxed text-amber-900 dark:text-amber-200">{cancellationWarning}</p>
                  <button
                    type="button"
                    onClick={() => { onClose(); navigate("/billing"); }}
                    className="text-xs font-semibold text-amber-800 dark:text-amber-200 underline underline-offset-2 hover:no-underline"
                  >
                    {t("mentoringBooking", "manageSubscription")}
                  </button>
                </div>
              </div>
            )}

            {/* Slot grid */}
            <div className="space-y-1.5">
              <p className="font-body text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("mentoringBooking", "pickSlot")}</p>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t("mentoringBooking", "loadingSlots")}
                </div>
              ) : !day ? (
                <p className="font-body text-sm text-muted-foreground py-4">—</p>
              ) : daySlots.length === 0 ? (
                <p className="font-body text-sm text-muted-foreground py-4">{t("mentoringBooking", "noSlotsDay")}</p>
              ) : (
                <div className={cn("grid gap-2", inline ? "grid-cols-3" : "grid-cols-4")}>
                  {daySlots.map((s) => (
                    <button
                      key={s.start}
                      type="button"
                      onClick={() => setSelected(s.start)}
                      className={cn(
                        "px-1 py-2 rounded-lg border text-xs font-body tabular-nums transition-all min-h-[44px]",
                        selected === s.start
                          ? "bg-primary text-foreground border-primary font-bold shadow-xs"
                          : "bg-card border-border hover:border-primary",
                      )}
                    >
                      {fmtTime(s.start)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Sticky footer */}
      {hasQuota && (
        <div className={cn(
          "shrink-0 border-t border-border/60 bg-card/95 backdrop-blur space-y-2.5 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.08)]",
          inline ? "px-3.5 py-3" : "px-5 py-3",
        )}>
          <Textarea
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            rows={2}
            maxLength={400}
            placeholder={t("mentoringBooking", "topicPh")}
            className="resize-none text-sm"
          />
          <Button
            className="w-full rounded-full bg-primary text-foreground hover:bg-primary/90 font-bold min-h-[48px]"
            disabled={!selected || submitting || !!cancellationWarning}
            onClick={handleConfirm}
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t("mentoringBooking", "confirmingBtn")}</>
            ) : (
              t("mentoringBooking", "confirmBtn")
            )}
          </Button>
        </div>
      )}
    </>
  );
};

export default SessionBookingFlow;
